import React, { useState, useEffect } from 'react';
import { ArrowRight, Play, Star, Sparkles, Award } from 'lucide-react';
import { Button } from './ui/button';

export const Hero = () => {
  const [displayText, setDisplayText] = useState('');
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [showParagraph, setShowParagraph] = useState(false);

  const texts = [
    "Where Stars Meet Success",
    "Excellence in Every Partnership",
    "Delivering Impact That Matters",
    "Your Vision, Our Expertise"
  ];

  const paragraphs = [
    "Connecting brands with the right talent. From A-list celebrities to sporting legends, we handle the details so you get the results.",
    "Great events. Smart partnerships. Happy clients. We keep it simple and deliver what we promise.",
    "Zero drama. Just results that work. Your brand deserves partnerships that actually matter.",
    "When you need it done right, you call us. Events, endorsements, experiences – we make it happen."
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const currentText = texts[currentTextIndex];

    if (isTyping) {
      if (displayText.length < currentText.length) {
        const timeout = setTimeout(() => {
          setDisplayText(currentText.slice(0, displayText.length + 1));
        }, 100);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => {
          setShowParagraph(true);
          setTimeout(() => {
            setIsTyping(false);
          }, 5000);
        }, 1000);
        return () => clearTimeout(timeout);
      }
    } else {
      setShowParagraph(false);
      if (displayText.length > 0) {
        const timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 50);
        return () => clearTimeout(timeout);
      } else {
        setCurrentTextIndex((prev) => (prev + 1) % texts.length);
        setIsTyping(true);
      }
    }
  }, [displayText, currentTextIndex, isTyping, texts]);

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Mobile Optimization */}
      <div className="absolute inset-0">
        <img
          src="/images/hero-bg.jpg"
          alt="Hero Background"
          className="w-full h-full object-cover fixed top-0 left-0 z-0"
          style={{
            objectPosition: window.innerWidth <= 768 ? 'center center' : 'center 25%',
            objectFit: 'cover'
          }}
        />
        {/* Enhanced Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-gray-900/75 to-black/85 z-10"></div>

        {/* Subtle Gradient Orbs */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-amber-400/15 to-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-purple-400/10 to-pink-500/10 rounded-full blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-32 pb-20">
        <div className="space-y-10">


          {/* Main Heading with Typing Animation */}
          <div className="space-y-6">
            <div className="h-32 flex items-center justify-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight text-center min-h-[120px] flex items-center font-heading">
                <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                  {displayText}
                  <span className="inline-block w-1 h-12 md:h-16 lg:h-20 bg-amber-400 ml-2 animate-pulse"></span>
                </span>
              </h1>
            </div>

            <div className={`transition-all duration-1000 ${showParagraph ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                {paragraphs[currentTextIndex]}
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
            <Button
              onClick={() => scrollToSection('celebrity')}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-blue-500/25 px-8 py-4 text-lg group"
            >
              Explore Our Services
              <ArrowRight className="ml-2 icon-hero group-hover:translate-x-1 transition-transform duration-300" />
            </Button>

            <Button
              onClick={() => scrollToSection('contact')}
              variant="outline"
              size="lg"
              className="border-2 border-white/30 text-white hover:bg-white hover:text-gray-900 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 px-8 py-4 text-lg"
            >
              Get in Touch
            </Button>
          </div>

          {/* Stats as simple text */}
          <div className="pt-16 text-center">
            <p className="text-xl md:text-2xl text-gray-300 font-medium">
              30+ Years of Experience • 10+ Lac Tickets Sold • 300+ Celebrity Associations
            </p>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};